import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../context/SessionContext';
import { useBranding } from '../../context/BrandingContext';
import { createSessionPayment, createMercadoPagoPreference } from '../../services/api';
import './BillSplitOptions.css';

function BillSplitOptions({ totalUnpaid, diners, onPaymentCreated, onWaiterPayment }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const { paymentMethods } = useBranding();
  const [splitParticipants, setSplitParticipants] = useState(2);
  const [showEqualSplit, setShowEqualSplit] = useState(false);
  const [showWaiterOptions, setShowWaiterOptions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const myOrders = diners?.find(d => d.customerId === session?.customer?.customerId);
  const myTotal = myOrders?.subtotal || 0;
  const perPerson = parseFloat((totalUnpaid / splitParticipants).toFixed(2));

  const isMpEnabled = !!paymentMethods?.mercadoPago?.enabled;

  const handleDigitalPayment = async (splitMode, amount, participants) => {
    setLoading(true);
    setError(null);
    try {
      if (isMpEnabled) {
        // MercadoPago Checkout Pro flow
        const response = await createMercadoPagoPreference({
          sessionId: session?.sessionId,
          splitMode,
          payerName: session?.customer?.name || 'Guest',
          payerId: session?.customer?.customerId,
          splitParticipants: participants || undefined,
        });
        onPaymentCreated({
          provider: 'mercadopago',
          paymentId: response.paymentId,
          checkoutUrl: response.checkoutUrl,
          amount: response.amount,
          sessionId: session?.sessionId,
        });
      } else {
        // Line Checkout flow
        const response = await createSessionPayment({
          sessionId: session?.sessionId,
          splitMode,
          payerName: session?.customer?.name || 'Guest',
          payerId: session?.customer?.customerId,
          splitParticipants: participants || undefined,
        });
        if (response.success) {
          onPaymentCreated({
            provider: 'line',
            paymentId: response.paymentId,
            amount: response.amount,
            qr: response.qr,
            deeplink: response.deeplink,
            sessionId: session?.sessionId,
          });
        }
      }
    } catch (err) {
      console.error('Error creating session payment:', err);
      setError(t('bill.paymentError'));
    } finally {
      setLoading(false);
    }
  };

  const handleWaiterPayment = async (method) => {
    setLoading(true);
    try {
      const response = await createSessionPayment({
        sessionId: session?.sessionId,
        splitMode: 'pay_waiter',
        payerName: session?.customer?.name || 'Guest',
        payerId: session?.customer?.customerId,
        paymentMethod: method
      });

      if (response.success) {
        onWaiterPayment?.(response);
      }
    } catch (err) {
      console.error('Error creating waiter payment:', err);
      setError(t('bill.paymentError'));
    } finally {
      setLoading(false);
      setShowWaiterOptions(false);
    }
  };

  return (
    <div className="bill-split">
      <h4 className="bill-split-title">{t('bill.payOptions')}</h4>

      {error && <div className="bill-split-error" role="alert">{error}</div>}

      <div className="bill-split-options">
        {/* Pay all */}
        <button
          className="split-option"
          onClick={() => handleDigitalPayment('pay_all', totalUnpaid)}
          disabled={loading}
        >
          <span className="material-icons" aria-hidden="true">account_balance_wallet</span>
          <div className="split-option-text">
            <strong>{t('bill.payAll')}</strong>
            <small>{t('bill.payAllDesc')}</small>
          </div>
          <span className="split-amount">${totalUnpaid.toFixed(2)}</span>
        </button>

        {/* Equal split */}
        <div className="split-option-group">
          <button
            className="split-option"
            onClick={() => setShowEqualSplit(!showEqualSplit)}
            disabled={loading}
          >
            <span className="material-icons" aria-hidden="true">group</span>
            <div className="split-option-text">
              <strong>{t('bill.splitEqual')}</strong>
              <small>{t('bill.splitEqualDesc')}</small>
            </div>
            <span className="material-icons split-chevron" aria-hidden="true">
              {showEqualSplit ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {showEqualSplit && (
            <div className="split-equal-config">
              <div className="split-stepper">
                <span className="split-stepper-label">{t('bill.participants')}</span>
                <div className="split-stepper-controls">
                  <button
                    className="stepper-btn"
                    onClick={() => setSplitParticipants(Math.max(2, splitParticipants - 1))}
                  >
                    <span className="material-icons" aria-hidden="true">remove</span>
                  </button>
                  <span className="stepper-value">{splitParticipants}</span>
                  <button
                    className="stepper-btn"
                    onClick={() => setSplitParticipants(splitParticipants + 1)}
                  >
                    <span className="material-icons" aria-hidden="true">add</span>
                  </button>
                </div>
              </div>
              <div className="split-result">
                <span>${perPerson.toFixed(2)} {t('bill.perPerson')}</span>
              </div>
              <button
                className="split-confirm-btn"
                onClick={() => handleDigitalPayment('equal_split', perPerson, splitParticipants)}
                disabled={loading}
              >
                {t('cart.pay_now')} ${perPerson.toFixed(2)}
              </button>
            </div>
          )}
        </div>

        {/* Pay mine */}
        {myTotal > 0 && (
          <button
            className="split-option"
            onClick={() => handleDigitalPayment('pay_mine', myTotal)}
            disabled={loading}
          >
            <span className="material-icons" aria-hidden="true">person</span>
            <div className="split-option-text">
              <strong>{t('bill.payMine')}</strong>
              <small>{t('bill.payMineDesc')}</small>
            </div>
            <span className="split-amount">${myTotal.toFixed(2)}</span>
          </button>
        )}

        {/* Pay waiter */}
        <div className="split-option-group">
          <button
            className="split-option"
            onClick={() => setShowWaiterOptions(!showWaiterOptions)}
            disabled={loading}
          >
            <span className="material-icons" aria-hidden="true">storefront</span>
            <div className="split-option-text">
              <strong>{t('bill.payWaiter')}</strong>
              <small>{t('bill.payWaiterDesc')}</small>
            </div>
            <span className="material-icons split-chevron" aria-hidden="true">
              {showWaiterOptions ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {showWaiterOptions && (
            <div className="waiter-options">
              {paymentMethods?.cash?.enabled !== false && (
                <button
                  className="waiter-method-btn"
                  onClick={() => handleWaiterPayment('cash')}
                  disabled={loading}
                >
                  <span className="material-icons" aria-hidden="true">payments</span>
                  {t('bill.cash')}
                </button>
              )}
              {paymentMethods?.card?.enabled && (
                <button
                  className="waiter-method-btn"
                  onClick={() => handleWaiterPayment('card_physical')}
                  disabled={loading}
                >
                  <span className="material-icons" aria-hidden="true">credit_card</span>
                  {t('bill.cardPhysical')}
                </button>
              )}
              {paymentMethods?.transfer?.enabled && (
                <button
                  className="waiter-method-btn"
                  onClick={() => handleWaiterPayment('transfer')}
                  disabled={loading}
                >
                  <span className="material-icons" aria-hidden="true">account_balance</span>
                  {t('bill.transfer')}
                </button>
              )}
              {paymentMethods?.mercadoPago?.enabled && (
                <button
                  className="waiter-method-btn"
                  onClick={() => handleWaiterPayment('mercadopago')}
                  disabled={loading}
                >
                  <span className="material-icons" aria-hidden="true">qr_code_2</span>
                  {t('bill.mercadopago')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BillSplitOptions;
